/**
 * Supabase Auth API error codes and messages
 * @see https://supabase.com/docs/reference/javascript/auth-error-codes
 */

/** Error response structure from Supabase Auth API */
export interface SupabaseAuthError {
  code: string
  message: string
}

/** Known Supabase Auth error codes */
export const SupabaseAuthErrorCode = {
  INVALID_CREDENTIALS: 'invalid_credentials',
  USER_NOT_FOUND: 'user_not_found',
  EMAIL_NOT_CONFIRMED: 'email_not_confirmed',
  USER_ALREADY_EXISTS: 'user_already_exists',
  WEAK_PASSWORD: 'weak_password',
  SAME_PASSWORD: 'same_password',
  EMAIL_EXISTS: 'email_exists',
  PHONE_EXISTS: 'phone_exists',
  SESSION_NOT_FOUND: 'session_not_found',
  FLOW_STATE_NOT_FOUND: 'flow_state_not_found',
  FLOW_STATE_EXPIRED: 'flow_state_expired',
  SIGNUP_DISABLED: 'signup_disabled',
  USER_BANNED: 'user_banned',
  OVER_REQUEST_RATE_LIMIT: 'over_request_rate_limit',
  OVER_EMAIL_SEND_RATE_LIMIT: 'over_email_send_rate_limit',
  OVER_SMS_SEND_RATE_LIMIT: 'over_sms_send_rate_limit',
  OTP_EXPIRED: 'otp_expired',
  OTP_DISABLED: 'otp_disabled',
} as const

export type SupabaseAuthErrorCodeType =
  (typeof SupabaseAuthErrorCode)[keyof typeof SupabaseAuthErrorCode]

/** User-friendly error messages in Spanish */
const errorMessages: Record<string, string> = {
  [SupabaseAuthErrorCode.INVALID_CREDENTIALS]:
    'El correo electrónico o la contraseña son incorrectos. Revisa tus datos e inténtalo de nuevo.',
  [SupabaseAuthErrorCode.USER_NOT_FOUND]: 'No se encontró ninguna cuenta con este correo electrónico.',
  [SupabaseAuthErrorCode.EMAIL_NOT_CONFIRMED]:
    'Debes confirmar tu correo electrónico antes de iniciar sesión. Revisa tu bandeja de entrada.',
  [SupabaseAuthErrorCode.USER_ALREADY_EXISTS]: 'Ya existe una cuenta con este correo electrónico.',
  [SupabaseAuthErrorCode.WEAK_PASSWORD]:
    'La contraseña es demasiado débil. Usa al menos 8 caracteres con letras y números.',
  [SupabaseAuthErrorCode.SAME_PASSWORD]: 'La nueva contraseña debe ser diferente a la anterior.',
  [SupabaseAuthErrorCode.EMAIL_EXISTS]: 'Este correo electrónico ya está en uso.',
  [SupabaseAuthErrorCode.PHONE_EXISTS]: 'Este número de teléfono ya está en uso.',
  [SupabaseAuthErrorCode.SESSION_NOT_FOUND]: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
  [SupabaseAuthErrorCode.FLOW_STATE_NOT_FOUND]: 'El enlace ha expirado. Solicita uno nuevo.',
  [SupabaseAuthErrorCode.FLOW_STATE_EXPIRED]: 'El enlace ha expirado. Solicita uno nuevo.',
  [SupabaseAuthErrorCode.SIGNUP_DISABLED]: 'El registro de nuevas cuentas está deshabilitado temporalmente.',
  [SupabaseAuthErrorCode.USER_BANNED]: 'Tu cuenta ha sido suspendida. Contacta con soporte para más información.',
  [SupabaseAuthErrorCode.OVER_REQUEST_RATE_LIMIT]:
    'Has realizado demasiadas solicitudes. Espera unos minutos e inténtalo de nuevo.',
  [SupabaseAuthErrorCode.OVER_EMAIL_SEND_RATE_LIMIT]:
    'Se han enviado demasiados correos. Espera unos minutos e inténtalo de nuevo.',
  [SupabaseAuthErrorCode.OVER_SMS_SEND_RATE_LIMIT]:
    'Se han enviado demasiados mensajes SMS. Espera unos minutos e inténtalo de nuevo.',
  [SupabaseAuthErrorCode.OTP_EXPIRED]: 'El código de verificación ha expirado. Solicita uno nuevo.',
  [SupabaseAuthErrorCode.OTP_DISABLED]: 'La verificación por código está deshabilitada.',
}

const defaultErrorMessage = 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo más tarde.'

/**
 * Gets a user-friendly error message from a Supabase Auth error code
 */
export function getAuthErrorMessage(code: string): string {
  return errorMessages[code] ?? defaultErrorMessage
}

/**
 * Type guard to check if an error is a Supabase Auth error
 */
export function isSupabaseAuthError(error: unknown): error is SupabaseAuthError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as SupabaseAuthError).code === 'string'
  )
}
